import { Link, useNavigate } from "react-router-dom";
import { Bookmark, BookmarkCheck, NotebookPen } from "lucide-react";
import useStore from "../store/useStore";
import { isCreditsIncomplete } from "../data/shows";
import { toast } from "sonner";
import Poster from "./Poster";
import { TOAST_MS } from "../lib/constants";
import { lower } from "../lib/strings";

export default function ShowCard({ show, index = 0 }) {
  const navigate = useNavigate();
  const { savedShows, toggleSave } = useStore();
  const saved = savedShows.includes(show.id);
  const incomplete = isCreditsIncomplete(show);

  const handleSave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    toggleSave(show.id);
    toast(saved ? "removed" : "saved", { duration: TOAST_MS.NORMAL });
  };

  const handleLog = (e) => {
    e.preventDefault();
    e.stopPropagation();
    navigate(`/log/${show.id}`);
  };

  return (
    <Link
      to={`/show/${show.id}`}
      data-testid={`show-card-${show.id}`}
      className="block group relative border-b-2 border-white/15 hover:border-acid transition-colors py-5 px-5"
    >
      <div className="flex gap-4">
        <Poster show={show} size="md" />

        <div className="flex-1 min-w-0">
          <div className="font-mono text-[10px] text-white/40 tabular-nums mb-1">
            {String(index + 1).padStart(2, "0")} · {lower(show.theatre)}
          </div>

          <h3 className="font-display text-xl sm:text-2xl leading-[1] tracking-tight text-white group-hover:text-acid transition-colors break-words">
            {lower(show.title)}
          </h3>

          <div className="mt-2 font-mono text-[11px] text-white/60 space-y-0.5">
            <div>{lower(show.dateLabel)}</div>
            {show.director && (
              <div className="text-white/50">dir · {lower(show.director)}</div>
            )}
          </div>

          <p
            data-testid={`show-description-${show.id}`}
            className={`mt-2 font-mono text-[11px] leading-relaxed line-clamp-2 ${
              show.description ? "text-white/70" : "text-white/30 italic"
            }`}
          >
            {show.description || "description not added yet."}
          </p>

          <div className="mt-2 flex flex-wrap gap-1">
            {show.size && (
              <span className="font-mono text-[10px] uppercase tracking-wider px-1.5 py-0.5 border border-white/30 text-white/70">
                {show.size}
              </span>
            )}
            {incomplete && (
              <span
                data-testid={`credits-incomplete-pill-${show.id}`}
                className="font-mono text-[10px] uppercase tracking-wider px-1.5 py-0.5 border border-acid text-acid"
              >
                credits incomplete
              </span>
            )}
          </div>
        </div>
      </div>

      {/* quick actions */}
      <div className="mt-4 grid grid-cols-3 gap-2">
        <button
          data-testid={`open-${show.id}`}
          onClick={(e) => {
            e.preventDefault();
            navigate(`/show/${show.id}`);
          }}
          className="font-mono text-xs lowercase border-2 border-white px-3 py-2 hover:bg-white hover:text-black transition-colors"
        >
          open
        </button>
        <button
          data-testid={`save-${show.id}`}
          onClick={handleSave}
          aria-pressed={saved}
          className={`font-mono text-xs lowercase border-2 px-3 py-2 transition-colors flex items-center justify-center gap-1.5 ${
            saved
              ? "border-acid bg-acid text-black"
              : "border-white hover:bg-white hover:text-black"
          }`}
        >
          {saved ? <BookmarkCheck className="w-3.5 h-3.5" strokeWidth={2} /> : <Bookmark className="w-3.5 h-3.5" strokeWidth={2} />}
          {saved ? "saved" : "save"}
        </button>
        <button
          data-testid={`log-${show.id}`}
          onClick={handleLog}
          className="font-mono text-xs lowercase border-2 border-acid text-acid px-3 py-2 hover:bg-acid hover:text-black transition-colors flex items-center justify-center gap-1.5"
        >
          <NotebookPen className="w-3.5 h-3.5" strokeWidth={2} />
          log
        </button>
      </div>
    </Link>
  );
}
